"use client";

/**
 * Android/Chromium "Install as app" — programmatic capture of the browser's
 * `beforeinstallprompt` event so we can replace the easily-missed mini-infobar
 * with our own affordance and fire the NATIVE install dialog on tap.
 *
 * iOS has no equivalent (Safari never fires this), which is why iOS keeps the
 * manual Add-to-Home-Screen steps. Here the browser hands us a one-shot event
 * when the PWA is installable; we suppress its default banner, stash it, and
 * `prompt()` it later from a real user gesture.
 *
 * The event can fire before any React component mounts, so capture happens at
 * module scope (client-only) into a tiny pub/sub store the hook subscribes to.
 * `ensureInstallPromptListener()` is exported so a globally-mounted island can
 * register it as early as possible; it's idempotent.
 */

import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

declare global {
  interface WindowEventMap {
    /** Chromium-only: the browser deems the PWA installable. Not in lib.dom
     *  (never standardized), so typed here for the listener. */
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

// The one-shot deferred event (null until captured / after use / after install).
let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = (): void => { listeners.forEach((l) => { l(); }); };

let registered = false;
export function ensureInstallPromptListener(): void {
  if (registered || typeof window === "undefined") return;
  registered = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    // Suppress Chrome's default mini-infobar — we surface our own, more visible
    // "Install as app" prompt instead (the whole point of this module).
    e.preventDefault();
    deferred = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    notify();
  });
}

// Register eagerly on first client import, before any component mounts.
ensureInstallPromptListener();

/**
 * Whether a native install prompt is available, and an action to fire it.
 * `canInstall` is false on iOS/desktop-without-eligibility and once installed.
 * `promptInstall()` shows the OS install dialog and resolves with the outcome;
 * the deferred event is single-use, so it's cleared afterward.
 */
export function useInstallPrompt(): {
  canInstall: boolean;
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
} {
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    ensureInstallPromptListener();
    const update = (): void => { setCanInstall(deferred !== null && !installed); };
    listeners.add(update);
    update(); // sync in case the event already fired before this mounted
    return () => { listeners.delete(update); };
  }, []);

  const promptInstall = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    const evt = deferred;
    if (!evt) return "unavailable";
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    // Single-use — drop it whether accepted or dismissed so we don't re-prompt a
    // stale event (a fresh beforeinstallprompt fires if it becomes installable
    // again).
    deferred = null;
    notify();
    return outcome;
  }, []);

  return { canInstall, promptInstall };
}
